import React, {ReactElement} from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme
} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {debounce} from 'lodash';
import Stack from '@mui/material/Stack';
import TextInput from '@components/form/text-input/text-input/TextInput';
import ActionModalDialog from '@components/dialog/action-dialog/ActionModalDialog';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {NEW_ROLE_NAME} from '@shared/constants/RolesComponent';
import Search from '@components/search/Search';
import {useAppContext} from '@context/AppContext';
import Scrollbar from 'src/minimal-components/scrollbar';
import {
  PermissionsForm,
  PageLoaderGrid,
  MainGrid,
  TopActionGrid,
  RightGrid,
  PermissionsWrapperGrid,
  PermissionsLoaderContainerGrid,
  NoPermissionsContainerGrid,
  PermissionsContainerGrid,
  RoleNameTypographyGrid,
  ActionButtonsGrid
} from './RolesComponentStyles';
import {
  addNewRoleAndPermissions,
  deleteRoleAndPermissions,
  getAllPermissionsList,
  getRoleList,
  getRoleAndPermissionsData,
  updateRoleAndPermissions
} from '@shared/services/service-handlers/PrivateService';
// import Theme from 'src/styles/theme';
import {APP} from 'src/styles/variables';
import {BASE_ROUTE, BASE_ROUTE_MODULE, USER_ACCESS} from 'src/routes/BaseRoute';
import FormConstants from '@shared/constants/FormConstants';
import {useNavigate} from 'react-router-dom';
import AuthUtils from 'src/utils/AuthUtils';
import {LoadingButton} from '@mui/lab';
import Card from '@mui/material/Card';
import {PulseLoader} from 'react-spinners';

const RolesComponent = (): JSX.Element => {
  const [activeTab, setActiveTab] = React.useState<number | string | any>();
  const [selectedRolePage, setSelectedRolePage] = React.useState<any>('');
  const [roleList, setRoleList] = React.useState<any>({
    list: [],
    loader: true
  });
  const [permissionList, setPermissionList] = React.useState<any>([]);
  const [checkboxData, setCheckboxData]: any = React.useState({
    data: {},
    changedData: {}
  });
  const [roleName, setRoleName]: any = React.useState('');
  const [isAddingRole, setIsAddingRole]: any = React.useState(false);
  const [loader, setLoader] = React.useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = React.useState<boolean>(false);
  const [openDialog, setOpenDialog] = React.useState({
    confirmDialog: false,
    unsavedDialog: false,
    deleteErrorDialog: false
  });
  const [nextSelectedRoleData, setNextSelectedRoleData] = React.useState({
    item: {},
    index: 0,
    permissionRes: ''
  });
  const [dialogButtonLoader, setDialogButtonLoader] =
    React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [rolesLoader, setRolesLoader] = React.useState<boolean>(true);
  const [dialogInfo, setDialogInfo] = React.useState<any>({
    dialogTitle: '',
    dialogDescription: '',
    yesLabelText: '',
    noLabelText: '',
    yesButtonColor: '',
    noButtonColor: ''
  });
  const theme = useTheme();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const {control, setValue} = useForm({
    mode: 'onBlur'
  });
  const {
    appState: {applicationName}
  } = useAppContext();

  // Check this
  const permissionItems = (rows: any) => {
    let objArray: any = {};
    for (const moduleItem of rows) {
      for (const item of moduleItem.functions) {
        let childObj = {};
        let obj: any = {
          [item.function_label]: false
        };
        childObj = {...childObj, ...obj};
        obj = {
          [item.id]: childObj
        };
        objArray = {...objArray, ...obj};
        return objArray;
      }
    }
    return objArray;
  };

  const onRoleChange = async (
    item: any,
    permissionListData?: any,
    rolesDataList?: any
  ) => {
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${USER_ACCESS}?${
        FormConstants.USER_ACCESS.ROLES + '='
      }?${FormConstants.USER_ACCESS.SELECTED_ROLE}=${item.name}`,
      {
        state: {}
      }
    );

    if (!isAddingRole) {
      const list = permissionItems(
        permissionListData ? permissionListData : permissionList
      );
      setCheckboxData((prevState: any) => ({
        ...prevState,
        data: list
      }));
    }

    setActiveTab(item?.id);
    setSelectedRolePage(item);

    try {
      setLoader(true);
      const roleItemRes = await getRoleAndPermissionsData(
        item?.id,
        applicationName
      );
      setLoader(false);
      let finalPermList = {};
      for (const Item of roleItemRes.data.permissions) {
        const obj: any = {
          [Item.function_label]: true
        };
        finalPermList = {...finalPermList, [Item.id]: obj};
      }

      setCheckboxData((prevState: any) => ({
        ...prevState,
        data: {...prevState.data, ...finalPermList},
        changedData: {}
      }));

      // remove roles which having role name as NEW_ROLE_NAME
      const list = rolesDataList ? rolesDataList : roleList?.list;

      const updatedList: any = list?.filter(
        (role: any) => role.name_label !== NEW_ROLE_NAME
      );

      setRoleList((prevState: any) => ({
        ...prevState,
        list: updatedList
      }));
    } catch (error) {
      setLoader(false);
      const {message}: any = error;
      enqueueSnackbar(message, SnackBarConfig.getError());
    } finally {
      setRoleList((prevState: any) => ({
        ...prevState,
        loader: false
      }));
    }
  };

  const handleRoleChangeStep = (item: any, index: any, permissionRes: any) => {
    if (
      checkboxData.changedData &&
      Object.keys(checkboxData.changedData).length > 0
    ) {
      if (index !== activeTab) {
        setOpenDialog((prevState: any) => ({
          ...prevState,
          unsavedDialog: true
        }));

        setNextSelectedRoleData((prevState: any) => ({
          ...prevState,
          item,
          index,
          permissionRes
        }));
      }
    } else if (index !== activeTab) {
      setIsAddingRole(false);
      navigate(
        `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${USER_ACCESS}?${
          FormConstants.USER_ACCESS.ROLES + '='
        }?${FormConstants.USER_ACCESS.SELECTED_ROLE}=${item.name}`,
        {
          state: {}
        }
      );
      onRoleChange(item, permissionRes);
    }
  };

  const handleParentChange = (item: any, event: any) => {
    const functionsList = item?.functions || [];
    const checkedValue = event?.target?.checked;
    let parentChecked: any = {};

    functionsList.map((perm: any) => {
      parentChecked = {
        ...parentChecked,
        [perm.id]: {[perm.function_label]: checkedValue}
      };
    });

    if (checkedValue) {
      setCheckboxData((prevState: any) => ({
        ...prevState,
        data: {...prevState.data, ...parentChecked},
        changedData: {...prevState.changedData, ...parentChecked}
      }));
    } else {
      // remove all items from changedData
      let updatedChangedData = {};
      const permFuncIds: any = [];
      functionsList.filter((func: any) => permFuncIds.push(func.id.toString()));
      const objList = {...checkboxData?.changedData, ...parentChecked};
      if (Object.entries(objList).length) {
        Object.entries(objList).forEach(([id, value]: any) => {
          let obj = {};
          if (!permFuncIds.includes(id.toString())) {
            obj = {[id]: value};
            updatedChangedData = {...updatedChangedData, ...obj};
          }
          setCheckboxData((prevState: any) => ({
            ...prevState,
            data: {...prevState.data, ...parentChecked},
            changedData: updatedChangedData
          }));
        });
      }
    }
  };

  const handleParentCheck = (item: any, functionsList = []) => {
    const functionItem: any = [];
    if (checkboxData.data && Object.keys(checkboxData.data).length) {
      functionsList.forEach((func: any) => {
        Object.entries(checkboxData?.data).forEach(([id, value]: any) => {
          if (func.id.toString() === id.toString()) {
            if (value[func.function_label]) {
              functionItem.push(value[func.function_label]);
            }
          }
        });
      });
    }
    return functionItem.length === functionsList.length;
  };

  const handleIntermediateCheck = (item: any, functionsList = []) => {
    const functionItem: any = [];
    if (checkboxData.data && Object.keys(checkboxData.data).length) {
      functionsList.forEach((func: any) => {
        Object.entries(checkboxData?.data).forEach(([id, value]: any) => {
          if (func.id.toString() === id.toString()) {
            if (value[func.function_label]) {
              functionItem.push(value[func.function_label]);
            }
          }
        });
      });
    }

    if (functionItem.length) {
      if (functionItem.length === functionsList.length) {
        return false;
      }
      return true;
    }
  };

  const handleChildChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedPermFuncObj: any
  ) => {
    const {id: childId, function_label: childLabel} = selectedPermFuncObj;

    const childValue = checkboxData?.data?.[childId] || {};

    childValue[childLabel] = event.target.checked;

    let isExist: any = false;
    Object.entries(checkboxData?.changedData).forEach(([id, value]: any) => {
      if (id === childId.toString()) {
        isExist = true;
      }
    });

    if (isExist) {
      let updatedChangedData = {};

      Object.entries(checkboxData?.changedData).forEach(([id, value]: any) => {
        let obj = {};
        if (id !== childId.toString()) {
          obj = {[id]: value};
        }
        updatedChangedData = {...updatedChangedData, ...obj};
        setCheckboxData((prevState: any) => ({
          ...prevState,
          data: {...prevState.data, [childId]: childValue},
          changedData: updatedChangedData
        }));
      });
    } else {
      setCheckboxData((prevState: any) => ({
        ...prevState,
        data: {...prevState.data, [childId]: childValue},
        changedData: {...prevState.changedData, [childId]: childValue}
      }));
    }
  };

  const init = async () => {
    try {
      const description = FormConstants.COMMON.VIEWED_ROLES;
      const roleRes = await getRoleList(
        searchQuery,
        description,
        applicationName
      );
      setRoleList((prevState: any) => ({
        ...prevState,
        list: roleRes?.data?.rows
      }));
      setRolesLoader(false);

      const roles = AuthUtils.getUserAccess();

      // fetch all permissions
      if (roleRes?.data?.rows.length !== 0) {
        const permissionRes = await getAllPermissionsList(applicationName);

        setPermissionList(permissionRes?.data?.rows);

        // fetching the first role item's permission list by default
        if (roleRes?.data?.rows?.[0]) {
          if (roles) {
            const res = roleRes?.data?.rows.find(
              (item: any) => item.name === roles
            );

            onRoleChange(res, permissionRes?.data?.rows, roleRes?.data?.rows);
            AuthUtils.clearUserAccess();
          } else {
            onRoleChange(
              roleRes?.data?.rows[0],
              permissionRes?.data?.rows,
              roleRes?.data?.rows
            );
          }
        }
        setRoleList((prevState: any) => ({
          ...prevState,
          loader: false
        }));
        setRolesLoader(false);
      } else if (roleRes?.data?.rows.length === 0) {
        setPermissionList([]);
        setActiveTab(null);
        setSelectedRolePage('');
        setRoleList((prevState: any) => ({
          ...prevState,
          loader: false
        }));
        setRolesLoader(false);
      }
    } catch (e: any) {
      setRoleList((prevState: any) => ({
        ...prevState,
        loader: false
      }));
    }
  };

  React.useEffect(() => {
    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleAddNewRole = async (letNewArray: any) => {
    try {
      const newRoleRes = await addNewRoleAndPermissions(
        {
          role_name: roleName || selectedRolePage?.name_label,
          permission_ids: letNewArray
        },
        applicationName
      );
      // update roles list with newly created role
      const list = roleList?.list;
      const roleObjIndex: number = roleList?.list?.findIndex(
        (role: any) => role.name_label === NEW_ROLE_NAME
      );
      if (roleObjIndex > -1) {
        list[roleObjIndex].id = newRoleRes?.data?.id;
        list[roleObjIndex].name_label =
          roleName || selectedRolePage?.name_label;
      }
      setRoleList((prevState: any) => ({
        ...prevState,
        list
      }));

      // update active tab also
      setActiveTab(newRoleRes?.data?.id);
      setSubmitLoader(false);
      enqueueSnackbar(newRoleRes?.message, SnackBarConfig.getSuccess());
    } catch (error) {
      setSubmitLoader(false);
      const {message}: any = error;
      enqueueSnackbar(message, SnackBarConfig.getError());
    }
  };

  const handleUpdateRole = async (letNewArray: any) => {
    try {
      const updateRes = await updateRoleAndPermissions(
        selectedRolePage?.id,
        {
          role_name: roleName || selectedRolePage?.name_label,
          permission_ids: letNewArray
        },
        applicationName
      );

      // Update the role list, if role name gets changes
      const list = roleList?.list;
      const roleObjIndex: number = roleList?.list?.findIndex(
        (role: any) => role.id === selectedRolePage?.id
      );

      if (roleObjIndex > -1) {
        list[roleObjIndex].name_label =
          roleName || selectedRolePage?.name_label;
      }
      setRoleList((prevState: any) => ({
        ...prevState,
        list
      }));

      setSubmitLoader(false);
      enqueueSnackbar(updateRes?.message, SnackBarConfig.getSuccess());
    } catch (error) {
      setSubmitLoader(false);
      const {message}: any = error;
      enqueueSnackbar(message, SnackBarConfig.getError());
    }
  };

  const handlePermissionSubmit = async () => {
    setSubmitLoader(true);
    const letNewArray: any = [];
    Object.entries(checkboxData?.data).forEach(([id, value]: any) => {
      Object.entries(value).forEach(([labelKey, labelValue]: any) => {
        if (labelValue) {
          letNewArray.push(id);
        }
      });
    });

    if (isAddingRole) {
      handleAddNewRole(letNewArray);
    } else {
      handleUpdateRole(letNewArray);
    }

    setCheckboxData((prevState: any) => ({
      ...prevState,
      changedData: {}
    }));
    setIsAddingRole(false);
  };

  const handleChangeTextField = (e: any) => {
    setValue(selectedRolePage.role, e.target.value);
    setRoleName(e.target.value);
  };

  const handleNewRoleStep = () => {
    const newID = Date.now();
    const str = newID.toString();
    const resultID = str.substring(0, 5);

    const newRoleObj = {
      id: resultID,
      name_label: NEW_ROLE_NAME
    };
    setActiveTab(newRoleObj.id);
    const list = roleList.list;
    list.push(newRoleObj);
    setRoleList((prevState: any) => ({
      ...prevState,
      list
    }));
    setSelectedRolePage(newRoleObj);

    const listData = permissionItems(permissionList);

    setCheckboxData((prevState: any) => ({
      ...prevState,
      data: listData,
      changedData: {}
    }));
  };

  const handleNewRole = async () => {
    setIsAddingRole(true);
    if (
      checkboxData.changedData &&
      Object.keys(checkboxData.changedData).length > 0
    ) {
      setOpenDialog((prevState: any) => ({
        ...prevState,
        unsavedDialog: true
      }));
    } else {
      handleNewRoleStep();
    }
  };

  const handleDelete = async () => {
    setOpenDialog((prevState: any) => ({
      ...prevState,
      confirmDialog: true
    }));
    setDialogInfo({
      dialogTitle: `${t('PRIVATE.ROLES.DELETE_DIALOG.TITLE', {ns: 'private'})}`,
      dialogDescription:
        `${t('PRIVATE.ROLES.DELETE_DIALOG.DESCRIPTION', {ns: 'private'})}` +
        ' ' +
        selectedRolePage?.name_label +
        '?',
      yesLabelText: `${t('PRIVATE.ROLES.DELETE_DIALOG.YES_LABEL_TEXT', {
        ns: 'private'
      })}`,
      yesButtonColor: APP.PALETTE.ROLES.RED,
      noLabelText: `${t('PRIVATE.ROLES.DELETE_DIALOG.NO_LABEL_TEXT', {
        ns: 'private'
      })}`,
      noButtonColor: APP.PALETTE.ROLES.GRAY
    });
  };

  interface UserGroup {
    name_label: string;
  }

  const showGroupName = (data: UserGroup[]) => {
    return data.map((item: UserGroup) => ' ' + item?.name_label);
  };

  const handleAgreeBtnClick = async () => {
    setDialogButtonLoader(true);
    try {
      const deleteRes = await deleteRoleAndPermissions(
        selectedRolePage?.id,
        {}
      );

      setDialogButtonLoader(false);
      setOpenDialog((prevState: any) => ({
        ...prevState,
        confirmDialog: false
      }));
      setActiveTab('');
      enqueueSnackbar(deleteRes?.message, SnackBarConfig.getSuccess());

      const list = roleList?.list?.filter(
        (item: any) => item.id !== selectedRolePage?.id
      );

      setRoleList((prevState: any) => ({
        ...prevState,
        list
      }));

      if (list && list.length) {
        onRoleChange(list[0], '', list);
      }
    } catch (error) {
      setOpenDialog((prevState: any) => ({
        ...prevState,
        deleteErrorDialog: true
      }));
      const {data}: any = error;
      const {userGroup} = data;

      setDialogInfo({
        dialogTitle:
          `${t('PRIVATE.ROLES.DELETE_ROLES_ERROR_DIALOG.TITLE', {
            ns: 'private'
          })}` +
          ' ' +
          selectedRolePage?.name_label,
        dialogDescription:
          `${t('PRIVATE.ROLES.DELETE_ROLES_ERROR_DIALOG.DESCRIPTION', {
            ns: 'private'
          })}` +
          ' ' +
          showGroupName(userGroup) +
          ' User Group',
        noLabelText: `${t(
          'PRIVATE.ROLES.DELETE_ROLES_ERROR_DIALOG.NO_LABEL_TEXT',
          {
            ns: 'private'
          }
        )}`,
        noButtonColor: APP.PALETTE.ROLES.GRAY
      });
      setDialogButtonLoader(false);
      setOpenDialog((prevState: any) => ({
        ...prevState,
        deleteErrorDialog: false
      }));

      setRoleList((prevState: any) => ({
        ...prevState
      }));
    }
  };

  const dialogClose = () => {
    setOpenDialog((prevState: any) => ({
      ...prevState,
      confirmDialog: false,
      unsavedDialog: false
    }));
  };

  const proceedStep = () => {
    if (isAddingRole) {
      if (Object.keys(nextSelectedRoleData?.item).length !== 0) {
        const list = roleList.list;
        list.pop();
        onRoleChange(nextSelectedRoleData.item, nextSelectedRoleData.index, '');
        setIsAddingRole(false);
      } else {
        setNextSelectedRoleData((prevState: any) => ({
          ...prevState,
          item: '',
          index: '',
          permissionRes: ''
        }));
        const list = roleList.list;
        list.pop();
        setRoleList((prevState: any) => ({
          ...prevState,
          list
        }));
        onRoleChange(list[0], '', list);
        setIsAddingRole(false);
      }
    } else {
      const {item, permissionRes} = nextSelectedRoleData;
      onRoleChange(item, permissionRes);
    }
    dialogClose();
  };

  const handleCancelButton = () => {
    if (isAddingRole) {
      if (
        checkboxData.changedData &&
        Object.keys(checkboxData.changedData).length > 0
      ) {
        setOpenDialog((prevState: any) => ({
          ...prevState,
          unsavedDialog: true
        }));
      } else {
        setLoader(true);
        const list = roleList.list;
        list.pop();
        setRoleList((prevState: any) => ({
          ...prevState,
          list
        }));
        onRoleChange(roleList?.list?.[0], '', roleList?.list);
        setIsAddingRole(false);
        setLoader(false);
      }
    } else if (
      checkboxData.changedData &&
      Object.keys(checkboxData.changedData).length > 0
    ) {
      setOpenDialog((prevState: any) => ({
        ...prevState,
        unsavedDialog: true
      }));
      setNextSelectedRoleData((prevState: any) => ({
        ...prevState,
        item: selectedRolePage,
        index: selectedRolePage?.id,
        permissionRes: ''
      }));
    } else {
      setNextSelectedRoleData((prevState: any) => ({
        ...prevState,
        item: selectedRolePage,
        index: selectedRolePage?.id,
        permissionRes: ''
      }));
    }
  };

  const renderRoleList = (item: any, index: number): any => {
    return (
      <div key={item?.id}>
        <ListItem key={item?.id} disablePadding sx={{display: 'block'}}>
          <ListItemButton
            sx={{
              minHeight: 48,
              paddingRight: 0,
              marginLeft: 1.8
            }}
            selected={index === activeTab}
            onClick={() => {
              handleRoleChangeStep(item, index, '');
            }}
            style={{
              backgroundColor: index === activeTab ? 'white' : 'none',
              borderRight:
                index === activeTab
                  ? `5px solid ${theme.palette.primary.dark}`
                  : `5px solid ${theme.palette.common.white}`
            }}
            autoFocus={index === activeTab ? true : false}>
            <ListItemText
              primaryTypographyProps={{
                style: {
                  color:
                    index === activeTab
                      ? theme.palette.primary.dark
                      : theme.palette.common.black
                },
                fontSize: 15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              primary={
                isAddingRole && index === activeTab
                  ? item?.name_label + `*`
                  : item?.name_label
              }
            />
          </ListItemButton>
        </ListItem>
        <Divider style={{width: '100%'}} />
      </div>
    );
  };

  const handleChangeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 2) {
      setSearchQuery(e.target.value);
    } else {
      setSearchQuery('');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeHandler = React.useCallback(
    debounce(handleChangeText, 300),
    []
  );

  const renderPermissionList = (item: any, index: any) => {
    return (
      <PermissionsForm key={item?.id} onSubmit={handlePermissionSubmit}>
        <FormControlLabel
          key={item?.id}
          label={
            <span style={{fontWeight: 'bold', overflow: 'none'}}>
              {item.object_label}
            </span>
          }
          control={
            <Checkbox
              key={item?.id}
              name={`${item.object_label}`}
              sx={{
                color: APP.PALETTE.ROLES.GRAY_2
              }}
              indeterminate={handleIntermediateCheck(item, item?.functions)}
              checked={handleParentCheck(item, item?.functions)}
              onChange={(e: any) => {
                handleParentChange(item, e);
              }}
            />
          }
        />
        {item?.functions.map((subItem: any) => {
          return (
            <Box
              key={subItem?.id}
              sx={{display: 'flex', flexDirection: 'column', ml: 3}}>
              <FormControlLabel
                label={subItem.function_label}
                control={
                  <Checkbox
                    sx={{
                      color: APP.PALETTE.ROLES.GRAY_2
                    }}
                    checked={
                      !!checkboxData?.data?.[subItem.id]?.[
                        subItem.function_label
                      ]
                    }
                    onChange={e => handleChildChange(e, subItem)}
                  />
                }
              />
            </Box>
          );
        })}
      </PermissionsForm>
    );
  };

  const renderButtons = () => {
    return (
      <>
        <LoadingButton
          type="submit"
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={submitLoader || isAddingRole}
          sx={{
            mr: 1,
            bgcolor: theme.palette.primary.lighter
          }}>
          {t('PRIVATE.ROLES.DELETE', {ns: 'private'})}
        </LoadingButton>
        <LoadingButton
          type="submit"
          variant="outlined"
          onClick={() => {
            handleCancelButton();
          }}
          sx={{
            mr: 1,
            bgcolor: theme.palette.primary.light
            // backgroundColor: APP.PALETTE.COMMON.WHITE,
            // borderColor: APP.PALETTE.MODULE_LAYOUT_COLOR.RED_BUTTON,
            // '&:hover': {
            //   border: '1px solid',
            //   borderColor: APP.PALETTE.MODULE_LAYOUT_COLOR.RED_BUTTON
            // }
          }}
          disabled={submitLoader}>
          {t('PRIVATE.ROLES.CANCEL', {ns: 'private'})}
        </LoadingButton>
        <LoadingButton
          type="submit"
          loading={submitLoader}
          variant="contained"
          sx={{
            mr: 1,
            bgcolor: theme.palette.primary.dark
            // backgroundColor: `${APP.PALETTE.MODULE_LAYOUT_COLOR.BUTTON}`,
            // '&:hover': {
            //   backgroundColor: `${APP.PALETTE.MODULE_LAYOUT_COLOR.HOVER_BUTTON}`
            // }
          }}
          onClick={handlePermissionSubmit}
          disabled={submitLoader}>
          {t('PRIVATE.ROLES.SAVE_ROLE', {ns: 'private'})}
        </LoadingButton>
      </>
    );
  };

  const renderDialog = (dialogProps: any): ReactElement => {
    return <ActionModalDialog {...dialogProps} />;
  };

  const renderSearchField = (): React.ReactElement => {
    return (
      <Search
        handleChangeText={debouncedChangeHandler}
        fieldName={t('PRIVATE.ROLES.ROLE_SEARCH_FORM', {ns: 'private'})}
        submitLoading={submitLoader}
        placeHolder={t('PRIVATE.ROLES.SEARCH_ROLES', {ns: 'private'})}
        searchQuery={searchQuery}
      />
    );
  };

  const renderLeftSectionRoles = (): ReactElement => {
    return (
      <>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            pr: 1.5,
            pl: 1.5,
            height: `7.7vh`,
            borderBottom: `solid 1px ${theme.palette.divider}`
          }}>
          <h4
            style={{display: 'flex', marginLeft: '0.5rem', flexWrap: 'nowrap'}}>
            {t('PRIVATE.ROLES.ROLE_HEADING', {ns: 'private'})}
          </h4>
          <Box sx={{flexGrow: 1}} />
          <LoadingButton
            variant="contained"
            color="success"
            sx={{
              mr: 1,
              bgcolor: theme.palette.primary.main
            }}
            disabled={isAddingRole}
            onClick={handleNewRole}>
            {t('PRIVATE.ROLES.ADD_ROLE_TEXT', {ns: 'private'})}
          </LoadingButton>
        </Stack>
        <Box sx={{p: 2.5}}>{renderSearchField()}</Box>
        <Scrollbar
          sx={{
            pb: 1,
            minHeight: '100vh',
            overflowY: 'auto',
            scrollbarWidth: 'none'
          }}>
          {rolesLoader ? (
            <ComponentLoader />
          ) : (
            <List
              sx={{
                bgcolor: 'background.paper',
                width: '100%',
                padding: 0,
                flexWrap: 'nowrap'
              }}>
              {roleList?.list?.map((item: any) =>
                renderRoleList(item, item.id)
              )}
            </List>
          )}
        </Scrollbar>
      </>
    );
  };

  const renderPermissions = (renderItem: any) => {
    switch (renderItem) {
      case loader:
        return (
          <PermissionsLoaderContainerGrid>
            <ComponentLoader />
          </PermissionsLoaderContainerGrid>
        );
      case activeTab:
        return (
          <PermissionsContainerGrid>
            {permissionList.map((item: any, index: number) =>
              renderPermissionList(item, index)
            )}
          </PermissionsContainerGrid>
        );
      default:
        return (
          <NoPermissionsContainerGrid>
            <Typography variant="h2" sx={{color: APP.PALETTE.ROLES.GRAY_1}}>
              {t('PRIVATE.ROLES.NO_ROLES_FOUND', {ns: 'private'})}
            </Typography>
          </NoPermissionsContainerGrid>
        );
    }
  };

  const renderPermissionsSection = (): ReactElement => {
    return (
      <RightGrid item xs={10}>
        <TopActionGrid>
          <RoleNameTypographyGrid>
            {activeTab && selectedRolePage && (
              <Controller
                key={`${selectedRolePage.name_label}`}
                name={`${selectedRolePage.name_label}`}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <TextInput
                    size="small"
                    sx={{
                      '& legend': {display: 'none'},
                      '& fieldset': {top: 0},
                      backgroundColor: 'white'
                    }}
                    variant="outlined"
                    name={selectedRolePage?.name_label}
                    type={''}
                    error={!!error}
                    defaultValue={selectedRolePage?.name_label}
                    field={field}
                    onChange={e => handleChangeTextField(e)}
                  />
                )}
              />
            )}
          </RoleNameTypographyGrid>
          <ActionButtonsGrid>
            {activeTab ? renderButtons() : ''}
          </ActionButtonsGrid>
        </TopActionGrid>
        <Divider style={{width: '100%'}} />
        <PermissionsWrapperGrid>
          {renderPermissions(
            loader || activeTab || roleList?.list?.length === 0
          )}
        </PermissionsWrapperGrid>
      </RightGrid>
    );
  };

  return (
    <Stack
      component={Card}
      direction="row"
      sx={{margin: '0 24px', height: `79vh`}}>
      {!roleList.loader ? (
        <MainGrid container xs={12}>
          {renderDialog({
            open: openDialog.confirmDialog,
            onClose: dialogClose,
            title: dialogInfo?.dialogTitle,
            description: dialogInfo?.dialogDescription,
            yesLabelText: dialogInfo?.yesLabelText,
            noLabelText: dialogInfo?.noLabelText,
            yesButtonColor: dialogInfo?.yesButtonColor,
            noButtonColor: dialogInfo?.noButtonColor,
            handleAgreeBtnClick: handleAgreeBtnClick,
            handleCloseBtnClick: dialogClose,
            loader: dialogButtonLoader
          })}
          <Stack
            sx={{
              height: 1,
              flexShrink: 0,
              width: `16.2vw`,
              borderRight: `solid 1px ${theme.palette.divider}`,
              transition: theme.transitions.create(['width'], {
                duration: theme.transitions.duration.shorter
              })
            }}>
            {renderLeftSectionRoles()}
          </Stack>
          {renderPermissionsSection()}
        </MainGrid>
      ) : (
        <PageLoaderGrid>
          <PulseLoader color={theme.palette.primary.main} />
        </PageLoaderGrid>
      )}
      {/* Render unsaved changed Dialog */}
      {renderDialog({
        open: openDialog.unsavedDialog,
        onClose: dialogClose,
        title: `${t('PRIVATE.ROLES.UNSAVED_CHANGES.TITLE', {ns: 'private'})}`,
        description: `${t('PRIVATE.ROLES.UNSAVED_CHANGES.DESCRIPTION', {
          ns: 'private'
        })}`,
        yesLabelText: `${t('PRIVATE.ROLES.UNSAVED_CHANGES.YES_LABEL_TEXT', {
          ns: 'private'
        })}`,
        noLabelText: `${t('PRIVATE.ROLES.UNSAVED_CHANGES.NO_LABEL_TEXT', {
          ns: 'private'
        })}`,
        yesButtonColor: APP.PALETTE.ROLES.RED,
        noButtonColor: APP.PALETTE.ROLES.GRAY,
        handleAgreeBtnClick: proceedStep,
        handleCloseBtnClick: dialogClose
      })}
    </Stack>
  );
};

export default RolesComponent;
